/**
 * Admin portal navigation configuration (PBI 1050).
 *
 * Option B — domain-grouped sections. Dashboard stays top-level;
 * all other items live under collapsible sections that support
 * multiple open sections at once.
 */

import {
  IconDashboard,
  IconFileText,
  IconCalendarEvent,
  IconTemplate,
  IconUsers,
  IconSettings,
  IconSparkles,
  IconBraces,
  IconRobot,
  IconFlower,
  IconBuildingStore,
  IconCards,
  IconGift,
  IconTruckDelivery,
  IconLifebuoy,
  IconActivity,
  IconMailForward,
  IconMailCheck,
  IconMail,
  IconChartBar,
  IconPercentage,
  IconUnlink,
  IconUsersGroup,
} from '@tabler/icons-react';
import { APP_ROUTES } from '../../constants/routes';

/** localStorage key for persisting which sections are expanded. */
export const ADMIN_NAV_SECTIONS_OPEN_KEY = 'admin-nav-sections-open';

/** Shared Tabler icon component type (all icons export the same shape). */
type TablerIcon = typeof IconDashboard;

/** A single admin navigation link. */
export interface AdminNavItem {
  path: string;
  label: string;
  icon: TablerIcon;
  /** Empty array = visible to all admin roles. */
  roles: string[];
}

/** A collapsible group of admin navigation links. */
export interface AdminNavSection {
  id: string;
  label: string;
  items: AdminNavItem[];
}

/** Dashboard is always visible at the top level (no section wrapper). */
export const adminDashboardNavItem: AdminNavItem = {
  path: APP_ROUTES.adminRoot,
  label: 'Dashboard',
  icon: IconDashboard,
  roles: [],
};

/**
 * Domain-grouped navigation sections (Option B).
 * Section ids are stable keys for localStorage persistence.
 */
export const adminNavSections: AdminNavSection[] = [
  {
    id: 'customers-support',
    label: 'Customers & Support',
    items: [
      {
        path: '/admin/customers',
        label: 'Customers',
        icon: IconUsers,
        roles: ['SuperAdmin', 'SupportAgent'],
      },
      {
        path: APP_ROUTES.adminSupportTickets,
        label: 'Support Tickets',
        icon: IconLifebuoy,
        roles: ['SuperAdmin', 'SupportAgent'],
      },
      {
        path: '/admin/audit-events',
        label: 'Audit Events',
        icon: IconFileText,
        roles: ['SuperAdmin', 'SupportAgent'],
      },
      {
        path: APP_ROUTES.adminEmailStats,
        label: 'Email Stats',
        icon: IconMail,
        roles: ['SuperAdmin', 'SupportAgent'],
      },
    ],
  },
  {
    id: 'orders-fulfillment',
    label: 'Orders & Fulfillment',
    items: [
      {
        path: APP_ROUTES.adminOperations,
        label: 'Scheduled Items',
        icon: IconActivity,
        roles: ['SuperAdmin', 'SupportAgent'],
      },
      {
        path: APP_ROUTES.adminFlowerOrders,
        label: 'Flower Orders',
        icon: IconTruckDelivery,
        roles: ['SuperAdmin', 'SupportAgent'],
      },
      {
        path: APP_ROUTES.adminMailedCardOrders,
        label: 'Mailed Card Orders',
        icon: IconCards,
        roles: ['SuperAdmin', 'SupportAgent'],
      },
      {
        path: APP_ROUTES.adminGiftOrders,
        label: 'Gift Orders',
        icon: IconGift,
        roles: ['SuperAdmin', 'SupportAgent'],
      },
    ],
  },
  {
    id: 'catalog-content',
    label: 'Catalog & Content',
    items: [
      {
        path: '/admin/templates',
        label: 'E-Card Templates',
        icon: IconTemplate,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: '/admin/mailed-cards',
        label: 'Mailed Card Artwork',
        icon: IconCards,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: APP_ROUTES.adminGifts,
        label: 'Gift catalog',
        icon: IconGift,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: '/admin/flowers/categories',
        label: 'Flower Catalog',
        icon: IconFlower,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: APP_ROUTES.adminFlowerVendors,
        label: 'Flower Vendors',
        icon: IconBuildingStore,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: APP_ROUTES.adminFlowerPromotions,
        label: 'Flower Promotions',
        icon: IconPercentage,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: '/admin/event-types',
        label: 'Event Types',
        icon: IconCalendarEvent,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: APP_ROUTES.adminContactRelationships,
        label: 'Contact Relationships',
        icon: IconUsersGroup,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: '/admin/placeholders',
        label: 'Template Placeholders',
        icon: IconBraces,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: '/admin/content-generation',
        label: 'Content Generation',
        icon: IconSparkles,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: APP_ROUTES.adminNurtureEmails,
        label: 'Nurture Emails',
        icon: IconMailForward,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: APP_ROUTES.adminGlockApps,
        label: 'Email Deliverability',
        icon: IconMailCheck,
        roles: ['SuperAdmin'],
      },
      {
        path: APP_ROUTES.adminMarketingPoas,
        label: 'Marketing POAS',
        icon: IconChartBar,
        roles: ['SuperAdmin'],
      },
    ],
  },
  {
    id: 'system-ai',
    label: 'System & AI',
    items: [
      {
        path: '/admin/ai-models',
        label: 'AI Models',
        icon: IconRobot,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: '/admin/site-settings',
        label: 'Site Settings',
        icon: IconSettings,
        roles: ['SuperAdmin', 'ContentManager'],
      },
      {
        path: APP_ROUTES.adminAssistantHosts,
        label: 'Assistant hosts',
        icon: IconUnlink,
        roles: ['SuperAdmin'],
      },
    ],
  },
];

/**
 * Returns true when the current pathname matches a nav item.
 * Uses startsWith for detail routes (e.g. /admin/support-tickets/:id).
 */
export const isAdminNavItemActive = (pathname: string, itemPath: string): boolean => {
  if (itemPath === APP_ROUTES.adminRoot) {
    return pathname === APP_ROUTES.adminRoot;
  }

  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
};

/**
 * Filters nav items to those visible for the given role set.
 */
export const filterAdminNavItemsByRoles = (
  items: AdminNavItem[],
  roles: string[],
): AdminNavItem[] =>
  items.filter((item) => item.roles.length === 0 || item.roles.some((r) => roles.includes(r)));

/**
 * Finds the section id containing the active route, if any.
 */
export const findActiveAdminNavSectionId = (
  pathname: string,
  sections: AdminNavSection[],
  roles: string[],
): string | null => {
  for (const section of sections) {
    const visibleItems = filterAdminNavItemsByRoles(section.items, roles);
    if (visibleItems.some((item) => isAdminNavItemActive(pathname, item.path))) {
      return section.id;
    }
  }

  return null;
};
